import { Navigate, NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Users, CreditCard, Mail, ScrollText, Shield, Settings as Cog, LogOut, Loader2, MessageSquare, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Header";

const ADMINS = ["owner", "admin"];
const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, roles: ADMINS, end: true },
  { to: "/admin/players", label: "Players", icon: Users, roles: [...ADMINS, "coach"] },
  { to: "/admin/payments", label: "Payments & Refunds", icon: CreditCard, roles: ADMINS },
  { to: "/admin/emails", label: "Emails", icon: Mail, roles: ADMINS },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, roles: ADMINS },
  { to: "/admin/giveaway", label: "Giveaway Entries", icon: Gift, roles: ADMINS },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText, roles: ADMINS },
  { to: "/admin/staff", label: "Staff & roles", icon: Shield, roles: ["owner"] },
  { to: "/admin/settings", label: "Camp settings", icon: Cog, roles: ADMINS },
];

export const PageHead = ({ title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <h1 className="font-display font-bold text-3xl tracking-tight">{title}</h1>
    <div className="flex gap-2 flex-wrap">{children}</div>
  </div>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin accent-text" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  const items = NAV.filter((n) => n.roles.includes(user.role));
  return (
    <div data-testid="admin-layout" className="min-h-screen md:flex">
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-white/10 p-4 md:p-5 md:sticky md:top-0 md:h-screen flex md:flex-col gap-4">
        <div className="flex items-center gap-3 md:mb-6"><Logo size={36} /><span className="font-display font-bold text-sm hidden md:block">EPL Admin</span></div>
        <nav className="flex md:flex-col gap-1 overflow-x-auto flex-1">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm whitespace-nowrap transition-colors ${isActive ? "bg-[var(--accent)] text-[#070a11] font-semibold" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={16} />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:block border-t border-white/10 pt-4">
          <p className="text-sm truncate" data-testid="admin-user-email">{user.email}</p>
          <p className="text-xs text-slate-500 capitalize">{user.role}</p>
          <button data-testid="admin-logout" onClick={logout} className="mt-3 flex items-center gap-2 text-sm text-slate-400 hover:text-white"><LogOut size={14} />Sign out</button>
        </div>
      </aside>
      <main className="flex-1 p-5 md:p-10 min-w-0">
        {user.role === "coach" && window.location.pathname === "/admin" ? <Navigate to="/admin/players" replace /> : <Outlet />}
      </main>
    </div>
  );
}
